import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import type { OperatorInsightDraft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export type InsightSurface =
  | "comments_chat"
  | "messenger_chat"
  | "landings_chat"
  | "inspiration_chat"
  | "bart"
  | "zespol"
  | "carousel"
  | "mapa_mysli";

type Props = {
  surface: InsightSurface;
  sourceRef?: string;
  messages?: { role: string; content: string }[];
  context?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
};

const KINDS = [
  { value: "conclusion", label: "Wniosek" },
  { value: "decision", label: "Decyzja" },
  { value: "hypothesis", label: "Hipoteza" },
  { value: "recommendation", label: "Rekomendacja" },
];

export default function SaveOperatorInsightButton({
  surface,
  sourceRef = "",
  messages,
  context,
  disabled,
  className,
  label = "Zapisz wniosek",
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<OperatorInsightDraft | null>(null);
  const [kind, setKind] = useState("conclusion");
  const [summary, setSummary] = useState("");
  const [tags, setTags] = useState("");
  const [bullets, setBullets] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  const draftM = useMutation({
    mutationFn: () =>
      api.operatorInsightsDraft({
        surface,
        source_ref: sourceRef,
        context,
        messages,
      }),
    onSuccess: (res) => {
      const d = res.draft;
      setDraft(d);
      setKind(d.kind || "conclusion");
      setSummary(d.summary || "");
      setTags((d.tags || []).join(", "));
      setBullets((d.bullets || []).join("\n"));
      setOpen(true);
      setError(null);
    },
    onError: (e: Error) => setError(e.message || "Nie udało się wygenerować draftu"),
  });

  const confirmM = useMutation({
    mutationFn: () =>
      api.operatorInsightsConfirm({
        surface,
        source_ref: sourceRef,
        kind,
        summary,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        draft_summary: draft?.summary,
        bullets: bullets
          .split("\n")
          .map((b) => b.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setSavedOk(true);
      setOpen(false);
      setDraft(null);
    },
    onError: (e: Error) => setError(e.message || "Zapis nieudany"),
  });

  useEffect(() => {
    if (!savedOk) return;
    const t = window.setTimeout(() => setSavedOk(false), 2500);
    return () => window.clearTimeout(t);
  }, [savedOk]);

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={className}
        disabled={disabled || draftM.isPending}
        onClick={() => draftM.mutate()}
      >
        {draftM.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
        {savedOk ? "Zapisano" : label}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl">
            <h2 className="text-base font-semibold text-slate-900">Edytuj wniosek przed zapisem</h2>
            <p className="mt-1 text-xs text-slate-500">
              Draft z modelu. Popraw treść i zatwierdź. Anuluj = bez zapisu.
            </p>

            <label className="mt-4 block text-xs font-medium text-slate-600">Rodzaj</label>
            <select
              className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>

            <label className="mt-3 block text-xs font-medium text-slate-600">Podsumowanie</label>
            <textarea
              className="mt-1 min-h-[120px] w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />

            <label className="mt-3 block text-xs font-medium text-slate-600">Tagi (przecinek)</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1" />

            <label className="mt-3 block text-xs font-medium text-slate-600">Punkty (opcjonalnie, linia = punkt)</label>
            <textarea
              className="mt-1 min-h-[72px] w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm"
              value={bullets}
              onChange={(e) => setBullets(e.target.value)}
            />

            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setOpen(false);
                  setDraft(null);
                  setError(null);
                }}
              >
                Anuluj
              </Button>
              <Button
                type="button"
                disabled={!summary.trim() || confirmM.isPending}
                onClick={() => confirmM.mutate()}
              >
                {confirmM.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
                Zatwierdź
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
